import React from "react";

/**
 * PaperPlaceholder — a deliberate stand-in for real cut-paper artwork.
 *
 * Per the graphics-overhaul brief: complex illustration is NOT produced in this
 * pass. Anywhere a bespoke paper graphic will eventually live (a hero scene, a
 * park badge, a category motif) we drop one of these instead — a translucent
 * colour field, sized to the final art, carrying a text label of what it
 * replaces. That makes every future swap-point obvious and keeps the *system
 * and layout* judgeable now without sinking a session into drawing.
 *
 * @param {string} label  - what the real art will be (e.g. "paper sun + tree")
 * @param {number|string} w - width  (number => px; string passed through, e.g. "100%")
 * @param {number|string} h - height (number => px; string passed through)
 * @param {string} tone   - CSS colour for the translucent fill (defaults to leaf)
 * @param {boolean} round - use the organic blob radius instead of a soft rect
 * @param {string} className
 * @param {object} style
 */
export default function PaperPlaceholder({
  label,
  w = "100%",
  h = 160,
  tone = "var(--color-primary)",
  round = false,
  className = "",
  style: externalStyle,
}) {
  const toLen = (v) => (typeof v === "number" ? `${v}px` : v);

  return (
    <div
      className={`paper-placeholder ${className}`}
      role="img"
      aria-label={`Placeholder: ${label}`}
      style={{
        width: toLen(w),
        height: toLen(h),
        // translucent fill so the underlying paper grain still reads through
        background: `color-mix(in srgb, ${tone} 22%, transparent)`,
        border: `2px dashed color-mix(in srgb, ${tone} 55%, transparent)`,
        borderRadius: round ? "var(--radius-blob)" : "var(--radius-lg)",
        color: "var(--color-text-light)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "var(--space-3)",
        fontFamily: "var(--font-body)",
        fontSize: "var(--font-size-sm)",
        fontWeight: 600,
        letterSpacing: "0.02em",
        lineHeight: 1.3,
        boxSizing: "border-box",
        ...externalStyle,
      }}
    >
      <span>
        <span aria-hidden="true" style={{ opacity: 0.6 }}>✂ </span>
        {label}
      </span>
    </div>
  );
}
